import PropTypes from 'prop-types';
import React, { Component } from 'react';
import { connect } from 'react-redux';

import { CHECKOUT_STEP_MAP, REVIEW_STEP, SHIP_OPTIONS_STEP } from 'constants/checkoutFlow';
import { SHIP_OPTION_GROUP_RETAIL_W_OPTIONS } from 'constants/shipOptionGroups';

import { trackEvent } from 'helpers/analytics';
import {
  advanceCheckout,
  fetchCheckoutData,
  setIsLoadingReviewSection
} from 'actions/checkout/purchase';
import { receiveNewShipOptionsField } from 'actions/checkout/shipOptions';
import BackButton from 'components/checkout/caap/BackButton';
import ContentBox from 'components/checkout/caap/ContentBox';
import ShippingOptions from 'components/checkout/caap/shipoption/ShippingOptions';
import ShippingOptionsModal from 'components/checkout/caap/shipoption/ShippingOptionsModal';
import { getFormattedDeliveryPromise } from 'components/checkout/CheckoutUtils';
import { toUSD } from 'helpers/NumberFormats';
import styles from 'styles/containers/checkout/shipOptionsSection.scss';

export class ShipOptionsSection extends Component {
  onShipOptionSelected = shipOptionId => {
    const { receiveNewShipOptionsField } = this.props;
    receiveNewShipOptionsField('shipOptionId', shipOptionId);
  }

  onShipOptionSelectedDesktop = shipOptionId => {
    const { setIsLoadingReviewSection, receiveNewShipOptionsField } = this.props;
    setIsLoadingReviewSection(true);
    receiveNewShipOptionsField('shipOptionId', shipOptionId);
    this.onUseShipOptionClick(shipOptionId);
  }

  onUseShipOptionClick = shipIdToUse => {
    const {
      advanceCheckout,
      checkoutData,
      fetchCheckoutData
    } = this.props;

    const {
      purchaseData: {
        data: { promise },
        isFetchingCheckoutData
      },
      shipOptions: { saved: savedShipOptions, new: newShipOption = {} }
    } = checkoutData;

    const { shipOptionId: selectedShipOptionId } = newShipOption;

    trackEvent('TE_CHECKOUT_SO_USE_OPTION');

    if (!isFetchingCheckoutData) {
      const currentShipOption = (savedShipOptions || []).find(
        item => item.promise === promise) || { id: null };

      if (typeof shipIdToUse === 'string') {
        if (shipIdToUse !== currentShipOption.id) {
          fetchCheckoutData({ shipmentOptionId: shipIdToUse, showFullPageSpinner: false, advanceOnSuccess: true });
        } else {
          advanceCheckout();
        }
      } else {
        if (selectedShipOptionId && selectedShipOptionId !== currentShipOption.id) {
          fetchCheckoutData({ shipmentOptionId: selectedShipOptionId, showFullPageSpinner: false, advanceOnSuccess: true });
        } else {
          advanceCheckout();
        }
      }
    }
  }

  render() {
    const {
      shipOptions,
      purchaseData: {
        data: {
          chargeSummary: { shippingCharge },
          deliveryPromise,
          purchaseId,
          promise,
          shipmentSpeed
        },
        isFetchingCheckoutData,
        pageHistory,
        purchaseShipOptionId,
        targetStep
      }
    } = this.props.checkoutData;

    const { isCondensed } = this.props;

    const {
      groups,
      new: newShipOption
    } = shipOptions;

    const shipOptionsForModal = groups[SHIP_OPTION_GROUP_RETAIL_W_OPTIONS] || { deliveryOptions: [] };

    if (!shipmentSpeed) {
      return null;
    }

    if (isCondensed) {
      return (
        <div>
          {
            deliveryPromise
              ? <div className={`${styles.deliveryPromise} ${styles.condensed}`}>
                {deliveryPromise}
              </div>
              : null
          }
        </div>
      );
    }

    const { testId = f => f } = this.context;
    const isNoBorder = true;
    const isSelectShipOptionStep = targetStep === SHIP_OPTIONS_STEP;
    const selectedShipOptionId = (newShipOption && newShipOption.shipOptionId)
      || purchaseShipOptionId
      || null;

    if (isSelectShipOptionStep) {
      return (
        <div>
          <div className={styles.hideSmallScreen}>
            {
              // would see this if user shrunk browser to mobile size, then opened the modal,
              // then made the browser large again - leaving the button with the options
              // so they aren't forced to click on the current item to make desktop checkout work
            }
            <ShippingOptionsModal
              isFetchingCheckoutData={isFetchingCheckoutData}
              formatPrice={formatPrice}
              onShipOptionSelected={this.onShipOptionSelectedDesktop}
              onUseShipOptionClick={this.onUseShipOptionClick}
              sectionKey="desktop"
              selectedShipOptionId={selectedShipOptionId}
              shipPromise={promise}
              shipOptions={shipOptionsForModal.deliveryOptions}
            />
          </div>

          <div className={styles.showOnlySmallScreen}>
            <ShippingOptionsModal
              isFetchingCheckoutData={isFetchingCheckoutData}
              formatPrice={formatPrice}
              onShipOptionSelected={this.onShipOptionSelected}
              onUseShipOptionClick={this.onUseShipOptionClick}
              sectionKey="mobile"
              selectedShipOptionId={selectedShipOptionId}
              shipPromise={promise}
              shipOptions={shipOptionsForModal.deliveryOptions}
            />
          </div>
        </div>
      );
    }

    // not select ship option modal...
    return (
      <div>
        <div className={styles.hideSmallScreen}>
          <ShippingOptionsModal
            formatPrice={formatPrice}
            hideContinueButton={true}
            isFetchingCheckoutData={isFetchingCheckoutData}
            onShipOptionSelected={this.onShipOptionSelectedDesktop}
            onUseShipOptionClick={this.onUseShipOptionClick}
            selectedShipOptionId={selectedShipOptionId}
            shipOptions={shipOptionsForModal.deliveryOptions}
            shipPromise={promise}
          />
          <hr />
          {
            deliveryPromise
              ? <div className={styles.deliveryPromise}>
                {deliveryPromise}
              </div>
              : null
          }
        </div>

        <div className={styles.showOnlySmallScreen}>
          <ContentBox
            isSolo={!isNoBorder}
            isNoPadding={true}
            isNoBorder={isNoBorder}
            isNonRadio={true}>
            {
              pageHistory && pageHistory.includes('spc')
                ? <BackButton href={CHECKOUT_STEP_MAP[REVIEW_STEP]} />
                : null
            }
            <ShippingOptions
              purchaseId={purchaseId}
              formatPrice={formatPrice}
              shipPromise={promise}
              shippingCharge={shippingCharge}
              shipmentSpeed={shipmentSpeed}
            />
          </ContentBox>

          {
            deliveryPromise
              ? <ContentBox
                isSolo={!isNoBorder}
                isNoPadding={true}
                isNoBorder={isNoBorder}
                isNonRadio={true}>
                <div className={styles.deliveryPromise}>
                  { formatDeliveryPromise(deliveryPromise, testId) }
                </div>
              </ContentBox>
              : null
          }
        </div>
      </div>
    );

  }
}

export const formatPrice = price => {
  if (typeof price === 'undefined') {
    return '';
  }
  return price === 0 ? '- FREE' : `- ${toUSD(price)}`;
};

export const formatDeliveryPromise = (deliveryPromise, testId) => {
  const updatedText = getFormattedDeliveryPromise(deliveryPromise);

  if (deliveryPromise.includes('Guaranteed')) {
    return (
      <div className={styles.deliveryPromiseDate}>
        <div>Guaranteed delivery:</div>
        <div data-test-id={testId('guaranteedDeliveryDate')}>{updatedText}</div>
      </div>
    );
  } else {
    return (
      <div className={`${styles.deliveryPromiseDate} ${styles.estimated}`}>
        <div>Estimated delivery:</div>
        <div data-test-id={testId('estimatedDeliveryDate')}>{updatedText}</div>
      </div>
    );
  }
};

function mapStateToProps(state) {
  return {
    checkoutData: state.checkoutData
  };
}

ShipOptionsSection.contextTypes = {
  testId: PropTypes.func
};

export default connect(mapStateToProps, {
  advanceCheckout,
  fetchCheckoutData,
  receiveNewShipOptionsField,
  setIsLoadingReviewSection
})(ShipOptionsSection);
